import {
  Breadcrumbs,
  Link as MuiLink,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  Paper,
} from "@mui/material"
import { Link, useParams } from "react-router-dom"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import { CrudProvider } from "@/context/crudContext"
import { DetailTable } from "@/components/Tables/DetailTable"
import { getColumns } from "@/pages/products/DetailColumns"
import OrderSummary from "@/components/Orders/OrderSummary"
import CustomerDetails from "@/components/Products/CustomerDetails"
import { isWeightable } from "@/lib/utils"
import { formatToDevanagariStyle } from "@/utilities/helper"

const OrderDetails = () => {
  const { id } = useParams()
  const { data: response, isLoading } = useCustomQuery(
    ["order-detail", id],
    `order/${id}`
  )

  if (isLoading) return <Typography>Loading...</Typography>

  const order = response?.data
  const returnItems =
    order?.sales_returns?.flatMap((salesReturn) =>
      salesReturn.items.map((item) => ({
        ...item,
        sales_return_code: salesReturn.code,
        sales_return_date: salesReturn.date,
      }))
    ) ?? []

  const returnItemColumn = [
    {
      header: "RETURN",
      accessorKey: "sales_return_code",
    },
    {
      header: "SKU",
      accessorKey: "sku",
      cell: ({ row }) => ` ${row.original.product.sku} %`,
    },
    {
      header: "CODE",
      accessorKey: "code",
      cell: ({ row }) => ` ${row.original.product?.code}`,
    },
    {
      header: "QUANTITY",
      accessorKey: "quantity",
    },
    {
      header: "PRICE",
      accessorKey: "total_price",
      cell: ({ row }) =>
        `Rs. ${formatToDevanagariStyle(row.original.total_price)}`,
    },
  ]

  return (
    <Box>
      {/* Breadcrumbs */}
      <Box sx={{ marginBottom: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <MuiLink component={Link} to="/pos/dashboard" underline="hover" color="inherit">
            Dashboard
          </MuiLink>
          <MuiLink
            component={Link}
            to="/pos/customers"
            underline="hover"
            color="inherit"
          >
            Orders
          </MuiLink>
          <Typography color="text.primary">{order.code}</Typography>
        </Breadcrumbs>
      </Box>

      {/* Page Title and Invoice Button */}
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: 3 }}
      >
        <Typography variant="h6" fontWeight={600}>
          Order Details
        </Typography>
        <Button
          component={Link}
          to={`/pos/invoice/${order.id}`}
          variant="contained"
          color="primary"
        >
          View Invoice
        </Button>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Product List Section */}
        <Grid item xs={12} md={8}>
          <Paper elevation={1} sx={{ paddingX: 3, paddingTop: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} marginBottom={2}>
              Products
            </Typography>
            <CrudProvider>
              <ProductList order={order} isLoading={isLoading} />
            </CrudProvider>
          </Paper>
          {returnItems.length > 0 && (
            <Paper elevation={1} sx={{ paddingX: 3, paddingTop: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} marginBottom={2}>
                Return Items
              </Typography>
              <DetailTable
                label="Return Items"
                columns={returnItemColumn}
                data={returnItems}
                isLoading={false}
              />
            </Paper>
          )}
        </Grid>

        {/* Sidebar: Customer + Summary */}
        <Grid item xs={12} md={4}>
          <Box display="flex" flexDirection="column" gap={3}>
            <CustomerDetails order={order} />
            {order.split_payments.length > 0 && (
              <Card
                sx={{
                  boxShadow: "none",
                  borderRadius: "7px",
                  mb: "25px",
                  padding: { xs: "18px", sm: "20px", lg: "25px" },
                }}
                className="rmui-card"
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  marginBottom={2}
                >
                  Payments
                </Typography>
                {order.split_payments.map((payment) => (
                  <div
                    key={payment.id}
                    className="flex justify-between items-center"
                  >
                    <Typography variant="body1" fontWeight={600}>
                      {payment.payment_method_name}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      Rs. {formatToDevanagariStyle(payment.amount)}
                    </Typography>
                  </div>
                ))}
              </Card>
            )}
            <OrderSummary order={order} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

function ProductList({ order, isLoading }) {
  const columns = getColumns(isWeightable)
  return (
    <DetailTable
      label="Orders"
      columns={columns}
      data={order.order_items}
      isLoading={isLoading}
    />
  )
}

export default OrderDetails
